import EquipmentMenuItem from './components/EquipmentMenuItem/EquipmentMenuItem';
import EquipmentSelectRejectedNotification from './components/EquipmentSelectRejectedNotification';
import { useShareAccessEquipmentGuideContext } from 'app/hooks/access-sharing';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ISharedEquipmentEntry } from 'types/IEquipmentEntry';
import './EquipmentSelect.less';
import clsx from 'clsx';
import { useNotificationsContext } from 'context';
import { SharedItem } from 'services/api/shares';

interface Props {
  equipment: ISharedEquipmentEntry[];
  selectedEquipmentIds: Set<string>;
  className?: string;
  type: 'equipment' | 'fleet';
  currentFleetId?: string;
  setSelectedEquipmentIds: (ids: Set<string>) => void;
  setIsSubmitDisabled: (value: boolean) => void;
}

export default function EquipmentSelect({
  equipment,
  selectedEquipmentIds,
  className,
  type,
  currentFleetId,
  setSelectedEquipmentIds,
  setIsSubmitDisabled,
}: Props) {
  const { t } = useTranslation();

  const { removeSnackbar, addSnackbar } = useNotificationsContext();

  const { openGuide } = useShareAccessEquipmentGuideContext();

  const sharedIds = useMemo(() => {
    return new Set(
      type === 'equipment'
        ? []
        : equipment
            .filter(equipment => equipment.isSharedIndividually)
            .map(equipment => equipment.id),
    );
  }, [equipment, type]);

  function isInAnotherFleet(fleetData: SharedItem | undefined) {
    if (!fleetData || !currentFleetId) return false;

    return fleetData.sharedItemDetails.exposedId !== currentFleetId;
  }

  const isAllDisabled = useMemo(
    () =>
      equipment.every(
        equipment =>
          sharedIds.has(equipment.id) || isInAnotherFleet(equipment.fleetData),
      ),
    [equipment, sharedIds, currentFleetId],
  );

  const isAllChecked = useMemo(
    () =>
      equipment.every(
        equipment =>
          sharedIds.has(equipment.id) ||
          isInAnotherFleet(equipment.fleetData) ||
          selectedEquipmentIds.has(equipment.id),
      ),
    [equipment, sharedIds, selectedEquipmentIds, currentFleetId],
  );

  function handleCheckAll(e: React.ChangeEvent<HTMLInputElement>) {
    const { checked } = e.target;
    const newIds = new Set(selectedEquipmentIds);

    equipment.forEach(equipment => {
      const { id } = equipment;

      const isShared = sharedIds.has(id);
      const isInFleet = isInAnotherFleet(equipment.fleetData);
      const isDisabled = isShared || isInFleet;

      if (checked && !isDisabled) {
        newIds.add(id);
      } else if (!checked) {
        newIds.delete(id);
      }
    });

    setSelectedEquipmentIds(newIds);
  }

  function handleCheck(
    isChecked: boolean,
    equipmentId: string,
    fleetData: SharedItem | undefined,
  ) {
    const newIds = new Set(selectedEquipmentIds);

    if (!isChecked) {
      newIds.delete(equipmentId);
      setSelectedEquipmentIds(newIds);
      return;
    }

    const isShared = sharedIds.has(equipmentId);
    const isInFleet = isInAnotherFleet(fleetData);
    const isDisabled = isShared || isInFleet;

    if (isDisabled) {
      const timeout = 4000;

      setIsSubmitDisabled(true);
      setTimeout(() => {
        setIsSubmitDisabled(false);
      }, timeout);

      addSnackbar(
        <EquipmentSelectRejectedNotification
          title={
            isInFleet
              ? t('equipment_already_in_fleet', {
                  fleet: fleetData?.sharedItemDetails.sharedItemLabel,
                })
              : t('equipment_already_shared')
          }
          onLearnMore={() => {
            removeSnackbar();
            openGuide();
          }}
        />,
        'screen-centered',
        timeout,
      );

      return;
    }

    newIds.add(equipmentId);
    setSelectedEquipmentIds(newIds);
  }

  if (!equipment.length) {
    return <div>No options</div>;
  }

  return (
    <div className={clsx('equipment-select', className)}>
      <div className="equipment-select__select-all-block">
        <label htmlFor="selectAll">{t('all')}</label>

        <input
          id="selectAll"
          type="checkbox"
          className="equipment-select__checkbox"
          checked={isAllChecked && !isAllDisabled}
          onChange={handleCheckAll}
        />
      </div>

      <ul className="equipment-select__list">
        {equipment.map(equipment => {
          const { id, fleetData } = equipment;

          const isChecked = selectedEquipmentIds.has(id);
          const isShared = sharedIds.has(id);
          const isInFleet = isInAnotherFleet(fleetData);

          return (
            <EquipmentMenuItem
              key={id}
              equipment={equipment}
              isChecked={isChecked}
              isShared={isShared}
              isInFleet={isInFleet}
              handleCheck={() => handleCheck(!isChecked, id, fleetData)}
            />
          );
        })}
      </ul>
    </div>
  );
}
